import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  brandFooterPage,
  getInfoPageTo,
  informationFooterPages,
  legalFooterPages,
} from "@modules/shop/config/info-pages";
import { showCookiePreferences } from "@modules/shopify/consent/ConsentProvider";
import { useLocale } from "@/modules/shared/hooks/useLocale";

const INSTAGRAM_URL = "https://www.instagram.com/postolar_vargaos/";
const FACEBOOK_URL = "https://www.facebook.com/PostolarVargaOsijek/";
const LINK_CLASS =
  "text-text-2 hover:text-text-1 text-small transition-colors duration-200 hover:underline";

export function Footer() {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <footer className="bg-background-7 mt-auto">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link
              to="/$locale"
              params={{ locale }}
              className="flex shrink-0 items-center"
            >
              <img
                src="/logo.svg"
                alt="Logo"
                width={70}
                height={40}
                className="text-primary dark:invert"
              />
            </Link>
            {brandFooterPage ? (
              <Link
                to={getInfoPageTo(brandFooterPage.path)}
                params={{ locale }}
                className="mt-3 inline-block text-text-3 hover:text-text-1 text-small transition-colors duration-200 hover:underline"
              >
                {t(`Footer.${brandFooterPage.footerKey}`)} →
              </Link>
            ) : null}
          </div>

          {/* Information */}
          <nav aria-label={t("Footer.information.title")}>
            <h4 className="text-text-2 text-small font-medium uppercase tracking-wider">
              {t("Footer.information.title")}
            </h4>
            <ul className="mt-4 space-y-3">
              {informationFooterPages.map((page) => (
                <li key={page.path}>
                  <Link
                    to={getInfoPageTo(page.path)}
                    params={{ locale }}
                    className={LINK_CLASS}
                  >
                    {t(`Footer.${page.footerKey}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label={t("StaticPages.groups.legal")}>
            <h4 className="text-text-2 text-small font-medium uppercase tracking-wider">
              {t("StaticPages.groups.legal")}
            </h4>
            <ul className="mt-4 space-y-3">
              {legalFooterPages.map((page) => (
                <li key={page.path}>
                  <Link
                    to={getInfoPageTo(page.path)}
                    params={{ locale }}
                    className={LINK_CLASS}
                  >
                    {t(`Footer.${page.footerKey}`)}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={showCookiePreferences}
                  className={`${LINK_CLASS} cursor-pointer`}
                >
                  {t("Footer.information.cookieSettings")}
                </button>
              </li>
            </ul>
          </nav>

          {/* Social */}
          <div>
            <h4 className="text-text-2 text-small font-medium uppercase tracking-wider">
              {t("Footer.social.title")}
            </h4>
            <div className="mt-4 flex items-center gap-5">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("Footer.social.followOnFacebook")}
                className="text-text-3 hover:text-text-1 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-5"
                  aria-hidden="true"
                >
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("Footer.social.followOnInstagram")}
                className="text-text-3 hover:text-text-1 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-5"
                  aria-hidden="true"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12s.014 3.668.072 4.948c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24s3.668-.014 4.948-.072c4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948s-.014-3.667-.072-4.947c-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border-5">
        <div className="mx-auto max-w-7xl px-6 py-6 lg:px-8">
          <p className="text-text-3 text-center text-small">
            {t("Footer.copyright", {
              year: new Date().getFullYear().toString(),
            })}
          </p>
        </div>
      </div>
    </footer>
  );
}
